import { memo, useState } from 'react';
import { Box, Typography, Paper, Slider, Tooltip, Button } from '@mui/material';
import AnimatedNumber from '../AnimatedNumber';

function RoyaltiesEstimator({ colors, user, navigate }) {
  const [trackCount, setTrackCount] = useState(10);
  const [streamCount, setStreamCount] = useState(10000);
  const RATE_PER_1000 = 1.5;
  const estimatedRoyalty = Math.round(trackCount * (streamCount / 1000) * RATE_PER_1000);
  return (
        <Box id="home-royalties" sx={{ py: { xs: 4, md: 7 } }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 1.5,
              color: colors.textPrimary,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Estimez vos royalties
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', color: colors.textSecondary, mb: 4, maxWidth: '600px', mx: 'auto' }}
          >
            Transformez ce manque à gagner en estimation concrète de ce que vos œuvres peuvent vous rapporter.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              bgcolor: colors.bgPaper,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
              p: { xs: 2.5, md: 3.5 },
              maxWidth: '920px',
              mx: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 0.75, fontWeight: 600 }}>
              Nombre de morceaux déposés
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
              {trackCount} {trackCount > 1 ? 'morceaux' : 'morceau'}
            </Typography>

            <Slider
              value={trackCount}
              onChange={(_, value) => setTrackCount(value)}
              min={1}
              max={100}
              step={1}
              sx={{
                color: colors.primaryLight,
                height: 8,
                '& .MuiSlider-thumb': {
                  width: 24,
                  height: 24,
                  bgcolor: colors.primaryLight,
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(129,140,248,0.2)',
                  },
                },
                '& .MuiSlider-track': { border: 'none' },
                '& .MuiSlider-rail': { bgcolor: colors.border },
                mb: 3,
              }}
            />

            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 0.75, fontWeight: 600 }}>
              Nombre d'écoutes par morceau
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5 }}>
              {streamCount.toLocaleString('fr-FR')} écoutes
            </Typography>

            <Slider
              value={streamCount}
              onChange={(_, value) => setStreamCount(value)}
              min={1000}
              max={1000000}
              step={1000}
              sx={{
                color: colors.secondary,
                height: 8,
                '& .MuiSlider-thumb': {
                  width: 24,
                  height: 24,
                  bgcolor: colors.secondary,
                  '&:hover': {
                    boxShadow: `0 0 0 8px ${colors.secondary}33`,
                  },
                },
                '& .MuiSlider-track': { border: 'none' },
                '& .MuiSlider-rail': { bgcolor: colors.border },
                mb: 3,
              }}
            />

            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'baseline',
              gap: 1,
              mb: 1.5,
            }}>
              <Typography variant="h3" sx={{ color: colors.success, fontWeight: 800 }}>
                <AnimatedNumber target={estimatedRoyalty} duration={500}
                  format={(n) => n.toLocaleString('fr-FR')} />€
              </Typography>
              <Typography variant="body1" sx={{ color: colors.textSecondary }}>
                / an
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Tooltip
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6, mb: 1 }}>
                      Montant net réparti calculé avec la valeur du stream de la dernière répartition,
                      ne garantissant pas le niveau des répartitions à venir. Des variations peuvent
                      être constatées en fonction des périodes d'exploitation. (Montant après les frais
                      de gestion et prélèvements au titre de l'action sociale et culturelle de la SACEM,
                      mais avant les cotisations et taxes obligatoires.)
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6, mb: 1 }}>
                      À noter : lorsque vous renseignez une œuvre, l'estimation calculée correspond à
                      la part qui vous revient. Si vous ne renseignez pas l'œuvre, l'estimation correspond
                      à la valeur totale de la diffusion.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6, mb: 1 }}>
                      Le style musical, en tant que tel, ne rentre pas dans le calcul de la répartition.
                      Il permet d'affiner le résultat selon les habitudes d'écoute des différents répertoires
                      constatées au sein des offres proposées par les plateformes (offre gratuite, abonnement
                      individuel, abonnement famille, abonnement étudiant, etc.).
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>
                      L'estimation proposée concerne uniquement les contenus musicaux écoutés en France sur
                      les principales plateformes listées.
                    </Typography>
                  </Box>
                }
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textSecondary,
                    textAlign: 'center',
                    cursor: 'help',
                    borderBottom: `1px dotted ${colors.textMuted}`,
                    display: 'inline-block',
                  }}
                >
                  Montant net réparti estimé *
                </Typography>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(user ? '/documents' : '/register')}
                sx={{
                  bgcolor: colors.primaryLight,
                  px: 4,
                  py: 1.4,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: colors.primaryLight,
                    boxShadow: '0 8px 24px rgba(129,140,248,0.4)',
                  },
                }}
              >
                {user ? 'Déclarer mes œuvres' : 'Récupérer mes droits'}
              </Button>
            </Box>
          </Paper>
        </Box>
  );
}

export default memo(RoyaltiesEstimator);
